import { Form, router, usePage } from "@inertiajs/react"
import { History, ListChecks, Loader2, Package, PencilLine } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useProductChoiceRefusal } from "@/components/shipments/product-choice-refusal"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { productChoiceShipmentPath } from "@/routes"
import type { ProductChoiceAction, ProductChoiceOffer } from "@/types/shipments"

/** The props a refusal's « Fermer » reloads — the queue moves, the offer does not. */
export const PRODUCT_CHOICE_RELOAD_ONLY = ["ready_parcels", "waiting", "counts"]

type OfferProduct = ProductChoiceOffer["products"][number]

/**
 * OKS-1040 (UX §2.3, UQ3) — « Choisir pour le créateur » / « Modifier le
 * choix ». The brand records a product choice the creator made off platform.
 *
 * Its own component, not the creator picker: the register differs (*vous*, the
 * creator named), and the contract differs — WARNINGS instead of refusals.
 * Every product stays selectable: an exhausted one carries a badge + helper,
 * one the creator already received carries the amber « déjà reçu » line (R13
 * posture — the creator is already granted).
 *
 * # The offer is fetched when the dialog opens
 *
 * `product_choice_offer` is an OPTIONAL prop (`Shipments::ProductChoiceOffer`),
 * loaded by a partial reload scoped to this collab, so a 25-row queue never
 * pays 25 offers on first paint. It is a PROP read, not the error bag.
 *
 * # Errors come from this form's own state
 *
 * Never `usePage().props.errors` (banned — `docs/frontend/inertia-error-bag.md`).
 * A dialog-level refusal (`already_chosen` / `offer_changed` / `not_shippable`)
 * renders as an Alert at the top, never a toast, and the submit becomes
 * « Fermer », which reloads the queue (UX §2.3).
 *
 * Inertia `<Form>` + plain `Label` / `RadioGroup` (Crew rule). No RHF, no zod.
 */
export function ProductChoiceDialog({
  collabId,
  action,
  creatorLabel,
  testIdPrefix,
}: {
  collabId: number
  action: ProductChoiceAction
  creatorLabel: string
  testIdPrefix: string
}) {
  const { t } = useTranslation()
  const pageOffer = usePage<{
    product_choice_offer?: ProductChoiceOffer | null
  }>().props.product_choice_offer
  // Only THIS collab's offer — another row's open dialog may have loaded its own.
  const offer = pageOffer?.collab_id === collabId ? pageOffer : null

  const [open, setOpen] = useState(false)
  // OKS-1040 (PO p4c-02a) — a refusal that lands after this row left its
  // section (the creator's pick settled it, unmounting the dialog) is shown by
  // the page instead. `null` outside the queue.
  const refusal = useProductChoiceRefusal()
  useEffect(() => {
    if (!open || !refusal) return
    return refusal.registerOpen()
  }, [open, refusal])
  const [step, setStep] = useState<"product" | "variant">("product")
  const [productId, setProductId] = useState("")
  const [variantId, setVariantId] = useState("")
  const [clientError, setClientError] = useState<string | null>(null)

  const chosen =
    offer?.products.find((product) => String(product.id) === productId) ?? null
  const needsVariant = chosen?.variant_step === true
  const isFinalStep = chosen != null && (step === "variant" || !needsVariant)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) refusal?.settled()
    setStep("product")
    setClientError(null)
    if (next) {
      // « Modifier le choix » opens pre-selected once the offer lands; the
      // pre-selection is applied from the offer below.
      setProductId("")
      setVariantId("")
      router.reload({
        only: ["product_choice_offer"],
        data: { product_choice_collab_id: collabId },
        preserveUrl: true,
        onSuccess: (page) => {
          const loaded = (
            page.props as { product_choice_offer?: ProductChoiceOffer }
          ).product_choice_offer
          if (loaded?.collab_id !== collabId) return
          setProductId(
            loaded.chosen_product_id ? String(loaded.chosen_product_id) : "",
          )
          setVariantId(
            loaded.chosen_variant_id ? String(loaded.chosen_variant_id) : "",
          )
        },
      })
    }
  }

  function closeAndReload() {
    handleOpenChange(false)
    router.reload({ only: PRODUCT_CHOICE_RELOAD_ONLY })
  }

  // ⚠️ OKS-1040 (PO 3.6, ≥90) — `preventDefault` is LOAD-BEARING. This
  // handler moves to the variant step, which re-renders THIS SAME button as
  // `type="submit"` before the browser runs the click's default action — so
  // without it the form submits an empty variant on « Continuer ». Not
  // observable in jsdom (its activation reads the type too early); the arm
  // that can red is the Playwright one.
  function advance(event: { preventDefault: () => void }) {
    event.preventDefault()
    if (!chosen) {
      setClientError(t("shipments.product_choice.errors.product_required"))
      return
    }
    setClientError(null)
    setStep("variant")
  }

  function validate(): boolean {
    if (!chosen) {
      setClientError(t("shipments.product_choice.errors.product_required"))
      return false
    }
    if (needsVariant && !variantId) {
      setClientError(t("shipments.product_choice.errors.variant_required"))
      return false
    }
    setClientError(null)
    return true
  }

  const choose = action === "choose"
  const TriggerIcon = choose ? ListChecks : PencilLine

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant={choose ? "default" : "ghost"}
            size="sm"
            className="max-sm:min-h-11 max-sm:w-full"
            // WCAG 2.5.3 — the accessible name contains the visible label.
            aria-label={
              choose
                ? t("shipments.product_choice.aria", { creator: creatorLabel })
                : t("shipments.product_choice.edit_aria", {
                    creator: creatorLabel,
                  })
            }
            data-testid={`${testIdPrefix}-product-choice-trigger`}
            data-action={action}
          >
            <TriggerIcon aria-hidden="true" />
            {choose
              ? t("shipments.product_choice.trigger")
              : t("shipments.product_choice.edit_trigger")}
          </Button>
        }
      />
      <DialogContent
        data-testid="shipments-product-choice-dialog"
        data-step={step}
        className="max-sm:h-dvh max-sm:max-w-none max-sm:rounded-none sm:max-w-md lg:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle>
            {step === "product"
              ? t("shipments.product_choice.title", { creator: creatorLabel })
              : t("shipments.product_choice.variant_title")}
          </DialogTitle>
          <DialogDescription>
            {step === "product"
              ? t("shipments.product_choice.description", {
                  creator: creatorLabel,
                })
              : t("shipments.product_choice.variant_body", {
                  product: chosen?.name ?? "",
                  creator: creatorLabel,
                })}
          </DialogDescription>
        </DialogHeader>

        <Form
          className="min-w-0"
          action={productChoiceShipmentPath(collabId)}
          method="post"
          options={{ preserveScroll: true }}
          onBefore={validate}
          onStart={() => refusal?.settled()}
          onError={(errs) => {
            const raw = (errs as Record<string, string | string[]>).base
            const base = Array.isArray(raw) ? raw[0] : raw
            if (base) refusal?.refused(base)
          }}
          onSuccess={() => handleOpenChange(false)}
          data-testid="shipments-product-choice-form"
        >
          {({ processing, errors }) => {
            const errorFor = (key: string): string | undefined => {
              const raw = (errors as Record<string, string | string[]>)?.[key]
              return Array.isArray(raw) ? raw[0] : raw
            }
            const baseError = errorFor("base")
            const fieldError =
              clientError ??
              errorFor("mission_product_id") ??
              errorFor("product_variant_id") ??
              null
            const errorId = "shipments-product-choice-error"

            return (
              <div className="flex flex-col gap-4">
                {baseError && (
                  <Alert
                    variant="destructive"
                    role="alert"
                    data-testid="shipments-product-choice-refusal"
                  >
                    <AlertDescription>{baseError}</AlertDescription>
                  </Alert>
                )}

                <input
                  type="hidden"
                  name="mission_product_id"
                  value={productId}
                />
                <input
                  type="hidden"
                  name="product_variant_id"
                  value={variantId}
                />

                {offer == null ? (
                  <p
                    className="text-muted-foreground flex items-center gap-2 text-sm"
                    aria-busy="true"
                    data-testid="shipments-product-choice-loading"
                  >
                    <Loader2
                      aria-hidden="true"
                      className="size-4 animate-spin"
                    />
                    <span className="sr-only">{t("shipments.loading")}</span>
                  </p>
                ) : (
                  <div className="max-h-[55vh] min-w-0 overflow-y-auto">
                    {step === "product" ? (
                      <ProductOptions
                        products={offer.products}
                        value={productId}
                        creatorLabel={creatorLabel}
                        errorId={fieldError ? errorId : undefined}
                        onChange={(next) => {
                          setProductId(next)
                          setVariantId("")
                          setClientError(null)
                        }}
                      />
                    ) : (
                      <VariantOptions
                        product={chosen}
                        value={variantId}
                        errorId={fieldError ? errorId : undefined}
                        onChange={(next) => {
                          setVariantId(next)
                          setClientError(null)
                        }}
                      />
                    )}
                  </div>
                )}

                {fieldError && (
                  <p
                    id={errorId}
                    aria-live="polite"
                    className="text-destructive text-sm"
                    data-testid="shipments-product-choice-field-error"
                  >
                    {fieldError}
                  </p>
                )}

                <DialogFooter className="max-sm:flex-col-reverse max-sm:gap-2">
                  {baseError ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="max-sm:min-h-11 max-sm:w-full"
                      onClick={closeAndReload}
                      data-testid="shipments-product-choice-close"
                    >
                      {t("shipments.product_choice.close")}
                    </Button>
                  ) : (
                    <>
                      {step === "variant" ? (
                        <Button
                          type="button"
                          variant="ghost"
                          className="max-sm:min-h-11 max-sm:w-full"
                          onClick={() => {
                            setStep("product")
                            setClientError(null)
                          }}
                          data-testid="shipments-product-choice-back"
                        >
                          {t("shipments.product_choice.back")}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          className="max-sm:min-h-11 max-sm:w-full"
                          onClick={() => handleOpenChange(false)}
                          data-testid="shipments-product-choice-cancel"
                        >
                          {t("shipments.product_choice.cancel")}
                        </Button>
                      )}
                      <Button
                        type={isFinalStep ? "submit" : "button"}
                        onClick={isFinalStep ? undefined : advance}
                        className="max-sm:min-h-11 max-sm:w-full"
                        disabled={processing || offer == null}
                        data-testid="shipments-product-choice-submit"
                      >
                        {processing && (
                          <Loader2
                            aria-hidden="true"
                            className="animate-spin"
                          />
                        )}
                        {processing
                          ? t("shipments.product_choice.submitting")
                          : t("shipments.product_choice.submit")}
                      </Button>
                    </>
                  )}
                </DialogFooter>
              </div>
            )
          }}
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function ProductOptions({
  products,
  value,
  creatorLabel,
  errorId,
  onChange,
}: {
  products: OfferProduct[]
  value: string
  creatorLabel: string
  errorId?: string
  onChange: (next: string) => void
}) {
  const { t } = useTranslation()

  return (
    <RadioGroup
      value={value}
      onValueChange={onChange}
      aria-label={t("shipments.product_choice.title", {
        creator: creatorLabel,
      })}
      aria-describedby={errorId}
      className="grid grid-cols-1 gap-2 lg:grid-cols-2"
    >
      {products.map((product) => {
        const id = `shipments-product-choice-${product.id}`
        const warningId = `${id}-warnings`
        const warned = product.exhausted || product.already_has

        return (
          <div
            key={product.id}
            data-testid="shipments-product-choice-option"
            data-exhausted={product.exhausted ? "true" : "false"}
            data-already-has={product.already_has ? "true" : "false"}
            className="flex min-w-0 items-start gap-3 rounded-md border p-3"
          >
            {/* ⚠️ NEVER disabled — warnings, not refusals (R13 posture). */}
            <RadioGroupItem
              value={String(product.id)}
              id={id}
              aria-describedby={warned ? warningId : undefined}
              className="mt-1 shrink-0"
            />
            <Label
              htmlFor={id}
              className="flex min-w-0 flex-1 cursor-pointer items-start gap-3 font-normal"
            >
              <ProductThumb product={product} />
              <span className="min-w-0 flex-1 space-y-1">
                <span className="block wrap-anywhere">{product.name}</span>
                <span className="text-muted-foreground block text-xs">
                  {t("collabs.drop_picker.quantity", {
                    count: product.quantity,
                  })}
                </span>
                {warned && (
                  <span id={warningId} className="block space-y-1">
                    {product.exhausted && (
                      <>
                        <Badge variant="destructive">
                          {t("shipments.product_choice.exhausted")}
                        </Badge>
                        <span
                          className="text-muted-foreground block text-xs"
                          data-testid="shipments-product-choice-exhausted-helper"
                        >
                          {t("shipments.product_choice.exhausted_helper")}
                        </span>
                      </>
                    )}
                    {product.already_has && (
                      <span
                        className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400"
                        data-testid="shipments-product-choice-already-has"
                      >
                        <History
                          aria-hidden="true"
                          className="size-3.5 shrink-0"
                        />
                        {t("shipments.product_choice.already_has", {
                          creator: creatorLabel,
                        })}
                      </span>
                    )}
                  </span>
                )}
              </span>
            </Label>
          </div>
        )
      })}
    </RadioGroup>
  )
}

function ProductThumb({ product }: { product: OfferProduct }) {
  const [errored, setErrored] = useState(false)
  const url = errored ? null : product.photo_url

  return (
    <span className="bg-muted text-muted-foreground flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md">
      {url ? (
        <img
          src={url}
          alt=""
          className="size-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        <Package aria-hidden="true" className="size-5" />
      )}
    </span>
  )
}

function VariantOptions({
  product,
  value,
  errorId,
  onChange,
}: {
  product: OfferProduct | null
  value: string
  errorId?: string
  onChange: (next: string) => void
}) {
  const { t } = useTranslation()
  if (!product) return null

  return (
    <RadioGroup
      value={value}
      onValueChange={onChange}
      aria-label={t("shipments.product_choice.variant_title")}
      aria-describedby={errorId}
      className="flex flex-col gap-2"
    >
      {product.variants.map((variant) => {
        const id = `shipments-product-choice-variant-${variant.id}`
        const label =
          [variant.title, variant.sku].filter(Boolean).join(" · ") ||
          t("collabs.drop_picker.unnamed_variant")

        return (
          <div
            key={variant.id}
            data-testid="shipments-product-choice-variant-option"
            className="flex min-w-0 items-center gap-3 rounded-md border p-3"
          >
            <RadioGroupItem
              value={String(variant.id)}
              id={id}
              className="shrink-0"
            />
            <Label
              htmlFor={id}
              className="min-w-0 flex-1 cursor-pointer font-normal wrap-anywhere"
            >
              {label}
            </Label>
          </div>
        )
      })}
    </RadioGroup>
  )
}
